import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import worldMap from "../../../assets/images/world_map.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faArrowRight,
  faUser,
  faPhone,
  faGlobe,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { authService } from "../../../services";
import Swal from "sweetalert2";
import { ScrollNavbar } from "../../../components";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../../contexts/AuthContext";

const Signup = () => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      Swal.fire({
        title: "Name is required",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (!formData.email) {
      Swal.fire({
        title: "Email is required",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (!formData.phone) {
      Swal.fire({
        title: "Phone number is required",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (!formData.password) {
      Swal.fire({
        title: "Password is required",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        title: "Password Mismatch",
        text: "Passwords do not match. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (!agreedToTerms) {
      Swal.fire({
        title: "Terms and Conditions",
        text: "You must agree to the terms and conditions to sign up.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#000000",
      });
      return;
    }

    setLoading(true);

    try {
      await authService.register(formData);

      await login(formData.email, formData.password);

      Swal.fire(
        "Success!",
        "You have successfully signed up and are now logged in.",
        "success"
      );

      const redirectPath = localStorage.getItem("redirect_path");
      if (redirectPath) {
        localStorage.removeItem("redirect_path");

        const redirectStateRaw = localStorage.getItem("redirect_state");
        localStorage.removeItem("redirect_state");
        let redirectState: unknown;
        if (redirectStateRaw) {
          try {
            redirectState = JSON.parse(redirectStateRaw);
          } catch {
            redirectState = undefined;
          }
        }

        history.replace(redirectPath, redirectState);
      } else {
        history.replace("/dashboard");
      }
    } catch (error: any) {
      Swal.fire(
        "Registration Failed",
        error.message || "Registration failed. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gray-50"
      style={{
        backgroundImage: `url(${worldMap})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <ScrollNavbar />
      <main className="flex-grow flex items-center justify-center p-4">
        <div
          className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200"
          data-aos="fade-up"
        >
          <h2 className="text-3xl font-mazzardbold mb-6 text-center text-gray-800">
            Create an Account
          </h2>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Name"
              name="name"
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              label="Email address"
              name="email"
              type="email"
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              label="Phone number"
              name="phone"
              type="tel"
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.phone}
              onChange={handleChange}
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className="mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  I agree to the{" "}
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    className="text-blue-800 hover:text-blue-900 font-semibold"
                  >
                    Terms and Conditions
                  </Link>
                </span>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? "Signing up..." : "Sign up"}
              {!loading && (
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              )}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to={"/login"}
              className="text-blue-800 hover:text-blue-900 font-semibold"
            >
              Log in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Signup;
